import { Button, Dialog, DialogActions, DialogContent, FormControl, Grid, makeStyles, MenuItem, Select, Typography } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PrintoutVersion, NodeTypeName, ExportType, ExportOptions, AllEditorialTypes, UserRole, User } from '../../domain/domain';
import { ApplicationState } from '../../reducers';
import { UiActionType } from '../../reducers/ui.reducer';
import HourglassSpinner from "../HourglassSpinner";
import ComponentSelectionPanel from './ComponentSelectionPanel';
import { SECTION_AND_BELOW_NODES, SHOW_HEADING_NODES } from './exportConstants';
import {
    handleExport,
    isEnableArticleAnalysis,
    isEnableCertifiedCopy,
    isEnableExportToXMLOrProvisionsPrintout,
    isEnableHeadingOnly,
    isEnableTitleAnalysis,
    isEnableUpdatesPrintout,
    isPrintDisabled,
    shouldUseComponentSelectionPanel
} from './exportHelpers';

const useStyles = makeStyles(_ => ({
    root: {
        overflow: 'hidden'
    },
    select: {
        width: 220
    }
}));

const PrintoutPanel: React.FC = () => {
    return (
        <Grid container direction='column' className="flex-gap-1">
            <Grid item>
            </Grid>
        </Grid>
    )
}

const ExportNodeDialog: React.FC = () => {
    let open = useSelector((state: ApplicationState) => state.ui.printingOptionsDialogOpen);
    let subTree = useSelector((state: ApplicationState) => state.ui.printingOptionsDialogSubTree);

    const currentUser: User | undefined = useSelector((state: ApplicationState) => state.user.currentUser);
    const isPubTeamMember = currentUser?.userRole !== UserRole.NON_PUBLICATIONS_TEAM;

    let dispatch = useDispatch();
    const classes = useStyles();
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedExportOptions, setSelectedExportOptions] = useState<ExportOptions | undefined>(undefined);

    useEffect(() => {
        // Set default export type based on user role and node type
        let defaultExportType = ExportType.PROVISIONS_PRINTOUT;
        if (isPubTeamMember) {
            defaultExportType = SECTION_AND_BELOW_NODES.includes(subTree?.nodeType ?? NodeTypeName.TITLE)
                ? ExportType.PROVISIONS_PRINTOUT
                : ExportType.CERTIFIED_COPY;
        }

        // Create base export options
        let exportOptions = {
            exportType: defaultExportType,
            citation: subTree ? subTree.citation : "",
            editorialContentPrintoutVersion: PrintoutVersion.NONE,
            editorialTypes: AllEditorialTypes,
            statuteVersion: PrintoutVersion.LAST_APPROVED_VERSION,
            hideHeadingsAndAnalyses: false
        };

        // For Provisions Printout: set Statute Version to None, Editorial Content to Working Copy
        if (defaultExportType === ExportType.PROVISIONS_PRINTOUT) {
            exportOptions = {
                ...exportOptions,
                statuteVersion: PrintoutVersion.NONE,
                editorialContentPrintoutVersion: PrintoutVersion.WORKING_COPY_VERSION
            };
        }
        if (defaultExportType === ExportType.CERTIFIED_COPY) {
            exportOptions = {
                ...exportOptions,
                editorialContentPrintoutVersion: PrintoutVersion.LAST_APPROVED_VERSION
            };
        }

        setSelectedExportOptions(exportOptions);
    }, [subTree, currentUser?.userRole, isPubTeamMember]);

    const updateExportOptions = (exportType: ExportType): void => {
        if (!selectedExportOptions) return;

        // Start with current values
        let statuteVersion = selectedExportOptions.statuteVersion;
        let editorialContentPrintoutVersion = selectedExportOptions.editorialContentPrintoutVersion;
        let hideHeadingsAndAnalyses = false;

        // Only set defaults for NEW selections, not when just changing a dropdown
        if (exportType !== selectedExportOptions.exportType) {
            if (exportType === ExportType.ARTICLE_ANALYSIS || exportType === ExportType.TITLE_ANALYSIS) {
                // Analysis types use Working Copy version
                statuteVersion = PrintoutVersion.WORKING_COPY_VERSION;
            } else if (exportType === ExportType.PROVISIONS_PRINTOUT) {
                // Only set defaults when first selecting Provisions Printout
                statuteVersion = PrintoutVersion.NONE;
                editorialContentPrintoutVersion = PrintoutVersion.WORKING_COPY_VERSION;
            } else if (exportType === ExportType.CERTIFIED_COPY) {
                // For Certified Copy, set specific defaults
                statuteVersion = PrintoutVersion.LAST_APPROVED_VERSION;
                editorialContentPrintoutVersion = PrintoutVersion.LAST_APPROVED_VERSION;
            }
        } else {
            // If just updating the current selection, preserve user's values
            hideHeadingsAndAnalyses = !!selectedExportOptions.hideHeadingsAndAnalyses;
        }

        const newExportOptions: ExportOptions = {
            ...selectedExportOptions,
            exportType,
            statuteVersion,
            editorialContentPrintoutVersion,
            hideHeadingsAndAnalyses
        };

        setSelectedExportOptions(newExportOptions);
    };

    const handleExportOptionsChange = (exportOptions: ExportOptions) => {
        setSelectedExportOptions({ ...exportOptions });
    };

    const renderPanel = () => {
        if (!selectedExportOptions) return null;

        if (shouldUseComponentSelectionPanel(selectedExportOptions.exportType)) {
            return (
                <ComponentSelectionPanel
                    exportOptions={selectedExportOptions}
                    onExportOptionsChanged={handleExportOptionsChange}
                />
            );
        } else {
            return <PrintoutPanel />;
        }
    };

    if (subTree === undefined) {
        return <></>
    }

    if (selectedExportOptions === undefined) {
        return <></>
    }

    return (
        <Dialog open={open}>
            <DialogContent style={{ overflow: 'hidden', width: 355 }}>
                {loading ? (
                    <HourglassSpinner message={"Generating printout..."} />
                ) : (
                    <Grid container direction='column' className={`${classes.root} flex-gap-2`}>
                        <Grid item>
                            <Typography variant='h3'>Print</Typography>
                            <Typography variant='h3' color="textSecondary">
                                {SHOW_HEADING_NODES.includes(subTree.nodeType)
                                    ? subTree.heading
                                    : subTree.citation}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Grid container direction='column' className="flex-gap-2">
                                <Grid item>
                                    <Grid container direction='row' className="flex-gap-1" alignItems='flex-end' alignContent='center'>
                                        <Grid item>
                                            <Grid container direction='column' className="flex-gap-1">
                                                <Grid item>
                                                    <Typography>Select Printout Type</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <FormControl size='small'>
                                                        <Select
                                                            value={selectedExportOptions?.exportType}
                                                            id='printout-type-select'
                                                            variant='outlined'
                                                            className={classes.select}
                                                            style={{ width: 300 }}
                                                            onChange={(event) => updateExportOptions(event.target.value as ExportType)}
                                                        >
                                                            {isPubTeamMember && isEnableExportToXMLOrProvisionsPrintout(subTree) &&
                                                                <MenuItem value={ExportType.EXPORT_TO_XML}>Export to XML</MenuItem>
                                                            }
                                                            {isPubTeamMember && isEnableCertifiedCopy(subTree) &&
                                                                <MenuItem value={ExportType.CERTIFIED_COPY}>Certified Copy</MenuItem>
                                                            }
                                                            {isEnableExportToXMLOrProvisionsPrintout(subTree) &&
                                                                <MenuItem value={ExportType.PROVISIONS_PRINTOUT}>Provisions Printout</MenuItem>
                                                            }
                                                            {isEnableTitleAnalysis(subTree) &&
                                                                <MenuItem value={ExportType.TITLE_ANALYSIS}>Title Analysis</MenuItem>
                                                            }
                                                            {isPubTeamMember && isEnableUpdatesPrintout(subTree) &&
                                                                <MenuItem value={ExportType.UPDATES_PRINTOUT}>Updates Printout</MenuItem>
                                                            }
                                                            {isEnableArticleAnalysis(subTree) &&
                                                                <MenuItem value={ExportType.ARTICLE_ANALYSIS}>Article Analysis</MenuItem>
                                                            }
                                                            {isEnableHeadingOnly(subTree) &&
                                                                <MenuItem value={ExportType.HEADING_ONLY}>Heading Only</MenuItem>
                                                            }
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    {renderPanel()}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button color='secondary' size='small' variant='contained'
                    onClick={() => {
                        dispatch({ type: UiActionType.PRINTING_OPTIONS_DIALOG_OPEN, open: false });
                    }}
                >Cancel</Button>
                {(selectedExportOptions.statuteVersion !== PrintoutVersion.NONE || selectedExportOptions.editorialContentPrintoutVersion !== PrintoutVersion.NONE) && (selectedExportOptions.exportType !== undefined) &&
                    <Button
                        color='primary'
                        size='small'
                        disabled={isPrintDisabled(selectedExportOptions, loading)}
                        variant='contained'
                        onClick={() => {
                            if (subTree) {
                                handleExport(subTree, selectedExportOptions, setLoading, dispatch);
                            }
                        }}
                    >
                        Print
                    </Button>
                }
            </DialogActions>
        </Dialog>
    )
}

export default ExportNodeDialog;